import { useRef } from 'react';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import {
  FieldError,
  SectionCard,
} from '@/features/campaigns/components/FormPrimitives';

type AuthorizationLetterSectionProps = {
  fileName: string | null;
  isUploading: boolean;
  error?: string;
  onSelect: (file: File) => void;
  onClear: () => void;
  variant?: 'card' | 'plain';
};

export function AuthorizationLetterSection({
  fileName,
  isUploading,
  error,
  onSelect,
  onClear,
  variant = 'card',
}: AuthorizationLetterSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <SectionCard
      step={4}
      title="Authorization letter"
      description="Optional — upload the client's authorization letter (PDF only)"
      variant={variant}
    >
      <div>
        {/* <FieldLabel>Authorization Letter</FieldLabel> */}
        {fileName ? (
          <div
            className={
              variant === 'plain'
                ? 'flex items-center gap-3 rounded-xl bg-[#EEF2F6] px-3.5 py-3'
                : 'flex items-center gap-3 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-3.5 py-3'
            }
          >
            <FileText className="h-5 w-5 shrink-0 text-[var(--dash-brand)]" />
            <div className="min-w-0 flex-1 text-left">
              <p className="m-0 truncate text-sm font-medium text-[var(--dash-ink)]">
                {fileName}
              </p>
              <p className="m-0 mt-0.5 text-[11px] text-[var(--dash-muted)]">PDF uploaded</p>
            </div>
            {isUploading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--dash-brand)]" />
            ) : (
              <button
                type="button"
                onClick={() => {
                  onClear();
                  if (inputRef.current) inputRef.current.value = '';
                }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--dash-muted)] transition hover:bg-white hover:text-[var(--dash-ink)]"
                aria-label="Remove authorization letter"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
            className={
              variant === 'plain'
                ? 'flex w-full flex-col items-center justify-center gap-2 rounded-xl bg-[#EEF2F6] px-4 py-6 text-center transition hover:bg-[#E4EAF0] disabled:opacity-50'
                : 'flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--dash-line)] bg-white px-4 py-8 text-center transition hover:border-[var(--dash-brand)] hover:bg-[var(--dash-soft)] disabled:opacity-50'
            }
          >
            {isUploading ? (
              <Loader2 className="h-7 w-7 animate-spin text-[var(--dash-brand)]" />
            ) : (
              <Upload className="h-7 w-7 text-[var(--dash-muted)]" />
            )}
            <span className="text-sm font-medium text-[var(--dash-ink)]">
              {isUploading ? 'Uploading…' : 'Click to upload PDF'}
            </span>
            <span className="text-[11px] text-[var(--dash-muted)]">PDF only · max 10MB</span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onSelect(file);
          }}
        />
        <FieldError message={error} />
      </div>
    </SectionCard>
  );
}
